import { Directive, DoCheck, ElementRef, OnDestroy, ViewChild, Renderer2, inject } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import each from 'lodash-es/each';
import includes from 'lodash-es/includes';
import { AbstractBraintreeComponent } from '@df/module-braintree/abstract-braintree.component';
import { Braintree } from '@df/module-braintree/braintree.namespace';

@Directive()
export abstract class AbstractBraintreeCardComponent
    extends AbstractBraintreeComponent<Braintree.ICreditCardPaymentMethod>
    implements OnDestroy, DoCheck
{
    /**
     * component validation model
     * @type {boolean}
     */
    valid?: boolean;

    /**
     * Hosted Fields submitted model
     * reset on selected method change
     * @type {boolean}
     */
    submitted = false;

    /**
     * 3DS in progress model
     * @type {boolean}
     */
    threeDSInProgress = false;

    /**
     * Hosted-fields instance
     * @type {Braintree.IHostedFieldsInstance | undefined}
     */
    hostedFieldsInstance: Braintree.IHostedFieldsInstance | undefined = undefined;

    /**
     * ThreeDS instance
     * @type {Braintree.IThreeDSInstance | undefined}
     */
    threeDSInstance: Braintree.IThreeDSInstance | undefined = undefined;

    /**
     * Payment method code related to component
     * @type {string}
     */
    protected readonly methodCode = Braintree.Methods.CreditCard;

    /**
     * Flag if classes was updated to prevernt permanent update
     */
    protected updateClassesFlag = false;

    get name(): string {
        return `braintree-${this.componentId}`;
    }

    protected renderer2 = inject(Renderer2);

    /**
     * Three ds container
     * important: you need to put #threedscontainer on div element in template if you want to use inline implementation for 3ds
     * not required for modal 3ds
     */
    @ViewChild('threedscontainer')
    protected threeDSContainer?: ElementRef<any>;

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.clearThreeDS(true);
        this.clearHostedFields().catch(() => {});
    }

    override ngDoCheck() {
        super.ngDoCheck();
        if (!this.isSelected()) {
            if (!this.updateClassesFlag) {
                this.submitted = false;
                this.updateFieldsClasses();
                this.updateClassesFlag = true;
            }
        } else {
            this.updateClassesFlag = false;
        }
    }

    /**
     * Normalize error message;
     * @param {any} e
     * @returns {string}
     */
    protected getError(e?: string | { error: string } | any): string {
        if (typeof e === 'string') {
            return e;
        } else if (e && typeof e.error === 'string') {
            return e.error;
        } else {
            return this.braintreeService.generalErrorMessage;
        }
    }

    //////////////////////////////////////////
    /////
    /////   Payment method configuration
    /////
    //////////////////////////////////////////

    /**
     * If we should use 3ds verification
     * @returns {boolean}
     */
    protected useThreeDS(countryId?: string): boolean {
        if (this.method && this.method.config['3ds']) {
            const thresholdAmount = this.method.config['3ds'].thresholdAmount || 0;
            const total = this.getTotal();
            if (total && total >= thresholdAmount) {
                const allowedCountries = this.method.config['3ds'].specificCountries;
                if (allowedCountries && allowedCountries.length) {
                    return includes(allowedCountries, countryId);
                } else {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Returns if we need hosted fields component
     * @returns {boolean}
     */
    protected abstract areHostedFieldsNeeded(): boolean;

    //////////////////////////////////////////
    /////
    /////   Script methods
    /////
    //////////////////////////////////////////

    /**
     * Returns required scripts for payment method
     * @returns {string[]}
     */
    protected getJsAssets(): string[] {
        return [Braintree.Scripts.CLIENT, Braintree.Scripts.HOSTED_FIELDS, Braintree.Scripts.THREE_DS];
    }

    /**
     * On script loasd success callback
     */
    protected override onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();

        if (this.areHostedFieldsNeeded()) {
            this.getHostedFieldsInstance()
                .then(hostedFieldsInstance => {
                    this.hostedFieldsInstance = hostedFieldsInstance;
                })
                .catch(() => this.onScriptLoadError());
        }
    }

    //////////////////////////////////////////
    /////
    /////   Hosted fields methods
    /////
    //////////////////////////////////////////

    /**
     * Returns tokenize payload promise
     * @param options
     * @returns {Promise<Braintree.ITokenizePayloadLike>}
     */
    protected abstract getTokenizePayloadLike(options: Braintree.ITokenizeOptions): Promise<Braintree.IHostedFieldsTokenizePayloadLike>;

    /**
     * Returns options for tokenization of hosted fields instance
     * @returns {Braintree.ITokenizeOptions}
     */
    protected abstract getTokenizeOptions(): Braintree.ITokenizeOptions;

    /**
     * Creates hosted fields instance
     * if already exists we teardown existing intance and create the new one after
     * @returns {Promise<Braintree.IHostedFieldsInstance>}
     */
    protected getHostedFieldsInstance(): Promise<Braintree.IHostedFieldsInstance> {
        return new Promise((resolve, reject) => {
            const initInstance = () => {
                this.braintreeService!.getHostedFields()!
                    .create(this.getHostedFieldsCreateOptions())
                    .then(instance => {
                        this.onHostedFieldsInstanceCreated(instance);
                        resolve(instance);
                    })
                    .catch(reject);
            };

            if (this.hostedFieldsInstance) {
                this.clearHostedFields()
                    .then(() => initInstance())
                    .catch(reject);
            } else {
                initInstance();
            }
        });
    }

    /**
     * Returns options for creating hosted fields instance
     * @returns {Braintree.IHostedFieldsOptions}
     */
    protected getHostedFieldsCreateOptions(): Braintree.IHostedFieldsOptions {
        return {
            authorization: this.getClientToken(),
            fields: this.getHostedFieldsOptions(),
            styles: this.getHostedFieldsStyles()
        };
    }

    /**
     * Clears hosted fields instance
     * @returns {Promise<undefined>}
     */
    protected clearHostedFields(): Promise<undefined> {
        return new Promise((resolve, reject) => {
            if (this.hostedFieldsInstance) {
                this.hostedFieldsInstance
                    .teardown()
                    .then(() => {
                        this.hostedFieldsInstance = undefined;
                        resolve(undefined);
                    })
                    .catch(reject);
            } else {
                resolve(undefined);
            }
        });
    }

    /**
     * Returns hosted-fields options for hosted-fields instance
     * @returns {Braintree.IHostedFieldsFieldOptions}
     */
    protected abstract getHostedFieldsOptions(): Braintree.IHostedFieldsFieldOptions;

    // An object that represents CSS that will be applied in each hosted field.
    // This object looks similar to CSS. Typically, these styles involve fonts (such as font-family or color).
    // https://braintree.github.io/braintree-web/current/module-braintree-web_hosted-fields.html#~styleOptions
    // https://developers.braintreepayments.com/guides/hosted-fields/styling/javascript/v3
    protected getHostedFieldsStyles(): any {
        return {};
    }

    /**
     * Hosted-fields intance created callback
     * can be used to add
     * @param hostedFieldsInstace
     */
    protected onHostedFieldsInstanceCreated(hostedFieldsInstace: Braintree.IHostedFieldsInstance): void {
        this.hostedFieldsInstance = hostedFieldsInstace;
        this.hostedFieldsInstance.on(Braintree.HostedFieldEventType.ValidityChange, state => this.onHostedFieldsStateUpdate(state));
        this.hostedFieldsInstance.on(Braintree.HostedFieldEventType.Empty, state => this.onHostedFieldsStateUpdate(state));
        this.hostedFieldsInstance.on(Braintree.HostedFieldEventType.NotEmpty, state => this.onHostedFieldsStateUpdate(state));
        this.hostedFieldsInstance.on(Braintree.HostedFieldEventType.Blur, state => this.onHostedFieldsStateUpdate(state));
        this.hostedFieldsInstance.on(Braintree.HostedFieldEventType.Focus, state => this.onHostedFieldsStateUpdate(state));
        this.onHostedFieldsStateUpdate();
    }

    /**
     * On hosted fields intance state update callback
     * @param {Braintree.IHostedFieldsInstance} state
     */
    protected onHostedFieldsStateUpdate(state = this.getHostedFieldsInstanceState()): void {
        if (state) {
            this.valid = !this.getFirstInvalid(state);
            this.updateFieldsClasses(state);
        } else {
            this.valid = false;
        }
        this.detectChanges();
    }

    /**
     * Returns hosted fields intance state
     * @returns {Braintree.IHostedFieldsInstance | undefined}
     */
    protected getHostedFieldsInstanceState(): Braintree.IHostedFieldsInstanceState | undefined {
        const instance = this.hostedFieldsInstance;
        return instance && instance.getState();
    }

    /**
     * Updates hosted fields containers classes
     * 'is-empty' - for empty fields
     * 'is-valid' - for valid fields
     * 'is-invalid' - for invalid fields, if there is no chance that fields will be valid or checkout submitted
     * @param {Braintree.IHostedFieldsInstanceState} hostedFieldsInstanceState
     */
    protected updateFieldsClasses(hostedFieldsInstanceState = this.getHostedFieldsInstanceState()): void {
        if (hostedFieldsInstanceState) {
            const emittedBy = hostedFieldsInstanceState.fields[hostedFieldsInstanceState.emittedBy!];

            const fields: Braintree.IHostedFieldsInstanceStateFieldsField[] = emittedBy
                ? [emittedBy]
                : Object.values(hostedFieldsInstanceState.fields);
            each(fields, field => {
                const { isEmpty, isValid, isPotentiallyValid, container, isFocused } = field;
                const parentElement = container.parentElement;
                const validState = isValid || (this.submitted ? false : isPotentiallyValid);
                each(
                    {
                        'is-empty': isEmpty,
                        'is-valid': isValid,
                        'is-invalid': !validState,
                        'is-focused': isFocused
                    },
                    (value, className) => {
                        value ? this.renderer2.addClass(parentElement, className) : this.renderer2.removeClass(parentElement, className);
                    }
                );
            });
        }
        this.detectChanges();
    }

    protected getFirstInvalid(state = this.getHostedFieldsInstanceState()): Braintree.IHostedFieldsInstanceStateFieldsField | undefined {
        if (state) {
            const fields = Object.values(state.fields) as Braintree.IHostedFieldsInstanceStateFieldsField[];
            return fields.find(field => !field.isValid);
        }
        return undefined;
    }

    /**
     * Validate hosted fields
     * @returns {boolean}
     */
    protected validateHostedFields(): boolean {
        const state = this.getHostedFieldsInstanceState();
        if (state) {
            this.updateFieldsClasses(state);
            const invalid = this.getFirstInvalid(state);
            if (!invalid) {
                return true;
            }
            if (this.scrollToInvalidActive()) {
                this.scrollToService.scrollTo(invalid.container);
            }
        }
        return false;
    }

    /**
     * If we should scroll to invalid field on validation
     * @returns {boolean}
     */
    protected scrollToInvalidActive(): boolean {
        return true;
    }

    //////////////////////////////////////////
    /////
    /////  3DS methods
    /////
    //////////////////////////////////////////

    protected getThreeDSInstance(version: Braintree.IThreeDSVersion = '2-inline-iframe'): Promise<Braintree.IThreeDSInstance> {
        return new Promise((resolve, reject) => {
            const initInstance = () => {
                this.braintreeService.getThreeDS()!.create({ authorization: this.getClientToken(), version }).then(resolve).catch(reject);
            };
            if (this.threeDSInstance) {
                this.clearThreeDS()
                    .then(() => initInstance())
                    .catch(() => initInstance());
            } else {
                initInstance();
            }
        });
    }

    /**
     * Returns html element for 3DS component
     * @returns {HTMLElement | undefined}
     */
    protected get threeDSContainerElement(): HTMLElement | undefined {
        return this.threeDSContainer && this.threeDSContainer.nativeElement;
    }

    /**
     * Clear threeDS instance
     * @returns {Promise<undefined>}
     */
    protected clearThreeDS(inProgress = false): Promise<undefined> {
        if (this.threeDSContainerElement) {
            this.threeDSContainerElement.innerHTML = '';
        }
        this.threeDSInProgress = inProgress;
        this.detectChanges();
        return new Promise(resolve => {
            const finish = () => {
                this.threeDSInstance = undefined;
                resolve(undefined);
            };
            if (this.threeDSInstance) {
                this.threeDSInstance
                    .teardown()
                    .then(() => finish())
                    .catch(() => finish());
            } else {
                finish();
            }
        });
    }

    /**
     * Returns 3DS verify options
     * @param {string} nonce
     * @param {string} bin
     * @returns {Braintree.IThreeDSVerifyCardOptions}
     */
    protected getThreeDSVerifyCardOptions(nonce: string, bin?: string): Braintree.IThreeDSVerifyCardOptions {
        const amount = this.getTotal()!.toString();
        const options: Braintree.IThreeDSVerifyCardOptions = { nonce, amount, additionalInformation: { acsWindowSize: '03' } };
        if (bin) {
            options.bin = bin;
        }
        return options;
    }

    protected onThreeDSInit(): void {
        const container = this.threeDSContainerElement;
        if (container) {
            this.scrollToService.scrollTo(container);
        }
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns submission payment data
     * @param {string} nonce
     * @param {boolean} saveCard
     * @returns {Checkout.ISubmissionPaymentData}
     */
    protected abstract getSubmissionPaymentData(
        nonce: string,
        saveCard: boolean
    ): Braintree.IPaymentSubmissionData<Braintree.Methods.CreditCard | 'vault'>;

    protected override onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }

        checkoutSubmitBeforeEventData.continueSubmission = false;
        this.submitted = true;
        if (this.validateHostedFields()) {
            const status = this.checkoutFormComponent.status;

            const tokenizeOptions = this.getTokenizeOptions();
            status.busy = true;

            const placeOrder = (nonce: string): void => {
                checkoutSubmitBeforeEventData.data.paymentMethod = this.getSubmissionPaymentData(nonce, tokenizeOptions.vault || false);
                status.data.busy = false;
                checkoutSubmitBeforeEventData.next();
            };

            this.getTokenizePayloadLike(tokenizeOptions)
                .then(tokenizePayload => {
                    const { nonce, details } = tokenizePayload;
                    const countryId =
                        checkoutSubmitBeforeEventData.data.billing_address && checkoutSubmitBeforeEventData.data.billing_address.country_id;
                    if (this.useThreeDS(countryId)) {
                        this.getThreeDSInstance()
                            .then(threeDSIntance => {
                                const onThreeDSError = () => {
                                    status.error = this.getError();
                                    this.clearThreeDS();
                                };

                                this.threeDSInstance = threeDSIntance;
                                threeDSIntance.on(
                                    Braintree.ThreeDSEventType.LookupComplete,
                                    (data: Braintree.IThreeDSVerificationData, next) => next()
                                );
                                threeDSIntance.on(
                                    Braintree.ThreeDSEventType.AuthenticationIframeAvailable,
                                    (event: Braintree.IThreeDSAuthenticationIframeAvailableEvent, next) => {
                                        this.threeDSContainerElement!.appendChild(event.element);
                                        this.threeDSInProgress = true;
                                        this.detectChanges();
                                        this.onThreeDSInit();
                                        next();
                                    }
                                );

                                threeDSIntance
                                    .verifyCard(this.getThreeDSVerifyCardOptions(nonce, details && details.bin))
                                    .then(verifyPayload => {
                                        const { liabilityShifted, liabilityShiftPossible } = verifyPayload.threeDSecureInfo;
                                        if (liabilityShifted || !liabilityShiftPossible) {
                                            placeOrder(verifyPayload.nonce);
                                            this.clearThreeDS(true);
                                        } else {
                                            onThreeDSError();
                                        }
                                    })
                                    .catch(onThreeDSError);
                            })
                            .catch(() => {
                                status.error = this.getError();
                            });
                    } else {
                        placeOrder(nonce);
                    }
                })
                .catch((e?: string) => {
                    if (typeof e === 'string') {
                        status.error = e;
                    } else {
                        status.busy = false;
                    }
                });
        }
    }

    /**
     * On checkout submit error callback
     * @param {Checkout.ISubmitErrorEventData} data
     */
    protected onCheckoutError(data: Checkout.ISubmitErrorEventData): void {
        this.clearThreeDS();
    }

    /**
     * On checkout step submission callback
     */
    protected override onStepSubmitted(): void {
        if (this.isSelected()) {
            this.submitted = true;
            this.updateFieldsClasses();
            this.detectChanges();
        }
    }
}
